import React, { useState, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Checkbox,
  FormControlLabel,
  Chip,
  LinearProgress,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { MainLayout } from '../components/MainLayout';
import {
  ImportExportService,
  type CSVPreviewResponse,
  type CSVImportResult,
} from '../services/import-export-service';
import { AccountsService } from '../services/accounts-service';
import type { Account } from '../types/api';
// The template-driven wizard replaced the fixed-format dropzone
// (BrokerCsvDropzone): instead of recognising three brokers by content, the
// user maps any broker's columns once and saves the mapping as a template.
import { GenericImportWizard } from '../components/GenericImportWizard';

const FIELD_OPTIONS = [
  { value: '', label: '-- Skip --' },
  { value: 'date', label: 'Date' },
  { value: 'amount', label: 'Amount (one signed column)' },
  // Most Canadian bank exports write two columns instead of one signed one,
  // with a positive magnitude in whichever applies. Mapping such a file to
  // "Amount" alone imported every withdrawal as a deposit.
  { value: 'amount_debit', label: 'Debit / Withdrawal (money out)' },
  { value: 'amount_credit', label: 'Credit / Deposit (money in)' },
  { value: 'balance', label: 'Running balance' },
  { value: 'description', label: 'Description' },
  { value: 'category', label: 'Category' },
  { value: 'payee_payer', label: 'Payee / Payer' },
  { value: 'reference_number', label: 'Reference #' },
  { value: 'transaction_type', label: 'Type (Deposit/Withdrawal)' },
];

const DATE_FORMATS = [
  { value: '2006-01-02', label: 'YYYY-MM-DD (2024-01-15)' },
  { value: '01/02/2006', label: 'MM/DD/YYYY (01/15/2024)' },
  { value: '1/2/2006', label: 'M/D/YYYY (1/15/2024)' },
  { value: '2006/01/02', label: 'YYYY/MM/DD (2024/01/15)' },
  { value: 'Jan 2, 2006', label: 'Mon D, YYYY (Jan 15, 2024)' },
  { value: '02-Jan-2006', label: 'DD-Mon-YYYY (15-Jan-2024)' },
];

export const ImportExportPage: React.FC = () => {
  const [tab, setTab] = useState(0);

  return (
    <MainLayout>
      <Box>
        <Typography variant="h4" fontWeight="bold" sx={{ mb: 1 }}>
          Import / Export
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Import transactions from CSV or export your data
        </Typography>

        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="Import CSV" icon={<UploadIcon />} iconPosition="start" />
          <Tab label="Brokerage import" icon={<UploadIcon />} iconPosition="start" />
          <Tab label="Export CSV" icon={<DownloadIcon />} iconPosition="start" />
        </Tabs>

        {tab === 0 && <ImportTab />}
        {tab === 1 && <GenericImportWizard />}
        {tab === 2 && <ExportTab />}
      </Box>
    </MainLayout>
  );
};

// ---- Import Tab ----

const ImportTab: React.FC = () => {
  const [step, setStep] = useState(0); // 0=upload, 1=map, 2=confirm, 3=result
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<CSVPreviewResponse | null>(null);
  const [allRows, setAllRows] = useState<string[][]>([]);
  const [columnMap, setColumnMap] = useState<Record<string, string>>({});
  const [dateFormat, setDateFormat] = useState('2006-01-02');
  const [skipDupes, setSkipDupes] = useState(true);
  // The statement's closing balance, as printed. Optional, and the only
  // thing that lets a manually imported account hold a real balance rather
  // than the net movement of whatever rows were in the file.
  const [closingBalance, setClosingBalance] = useState('');
  const [accountId, setAccountId] = useState('');
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CSVImportResult | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fetchAccounts = useCallback(async () => {
    try {
      const svc = new AccountsService();
      const res = await svc.getAccounts({ per_page: 100 });
      setAccounts(res.data.data || []);
    } catch {
      // silent
    }
  }, []);

  React.useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (!selected) return;
    setFile(selected);
    setError(null);
    setLoading(true);

    try {
      const res = await ImportExportService.previewCSV(selected);
      setPreview(res);

      // Read all rows from file for import
      const text = await selected.text();
      const lines = text.split('\n').filter((l) => l.trim());
      // Parse CSV manually for full rows (skip header)
      const rows: string[][] = [];
      for (let i = 1; i < lines.length; i++) {
        rows.push(parseCSVLine(lines[i]));
      }
      setAllRows(rows);

      // Auto-map columns by header name
      const autoMap: Record<string, string> = {};
      res.headers.forEach((h, idx) => {
        const lower = h.toLowerCase().trim();
        if (lower.includes('date')) autoMap[String(idx)] = 'date';
        // Debit/credit and balance are checked BEFORE the generic amount
        // match: Scotiabank writes "Withdrawal Amount" / "Deposit Amount",
        // and both contain "amount".
        else if (lower.includes('debit') || lower.includes('withdraw')) autoMap[String(idx)] = 'amount_debit';
        else if (lower.includes('credit') || lower.includes('deposit')) autoMap[String(idx)] = 'amount_credit';
        else if (lower.includes('balance')) autoMap[String(idx)] = 'balance';
        else if (lower.includes('amount') || lower.includes('total')) autoMap[String(idx)] = 'amount';
        else if (lower.includes('desc') || lower.includes('memo') || lower.includes('note')) autoMap[String(idx)] = 'description';
        else if (lower.includes('categ')) autoMap[String(idx)] = 'category';
        else if (lower.includes('payee') || lower.includes('payer') || lower.includes('merchant')) autoMap[String(idx)] = 'payee_payer';
        else if (lower.includes('ref') || lower.includes('check') || lower.includes('number')) autoMap[String(idx)] = 'reference_number';
        else if (lower.includes('type')) autoMap[String(idx)] = 'transaction_type';
      });
      setColumnMap(autoMap);
      setStep(1);
    } catch (err: any) {
      setError(err.message || 'Failed to preview CSV');
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    if (!accountId) {
      setError('Please select an account');
      return;
    }
    setLoading(true);
    setError(null);

    try {
      const res = await ImportExportService.importCSV({
        account_id: accountId,
        column_map: columnMap,
        rows: allRows,
        date_format: dateFormat,
        skip_duplicates: skipDupes,
        closing_balance: closingBalance.trim() || undefined,
      });
      setResult(res);
      setStep(3);
    } catch (err: any) {
      setError(err.message || 'Import failed');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setStep(0);
    setFile(null);
    setPreview(null);
    setAllRows([]);
    setColumnMap({});
    setResult(null);
    setError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const mappedFieldCount = Object.values(columnMap).filter((v) => v).length;
  const hasDate = Object.values(columnMap).includes('date');
  // Any of the three ways a file can express an amount satisfies the guard.
  // Requiring "amount" specifically blocked every debit/credit export.
  const mappedFields = Object.values(columnMap);
  const hasAmount =
    mappedFields.includes('amount') ||
    mappedFields.includes('amount_debit') ||
    mappedFields.includes('amount_credit');
  const hasBalanceColumn = mappedFields.includes('balance');

  return (
    <Box>
      <Stepper activeStep={step} sx={{ mb: 3 }}>
        <Step><StepLabel>Upload</StepLabel></Step>
        <Step><StepLabel>Map Columns</StepLabel></Step>
        <Step><StepLabel>Confirm</StepLabel></Step>
        <Step><StepLabel>Results</StepLabel></Step>
      </Stepper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Step 0: Upload */}
      {step === 0 && (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <UploadIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" sx={{ mb: 1 }}>
              Upload a CSV file
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              The first row should contain column headers
            </Typography>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              style={{ display: 'none' }}
              onChange={handleFileSelect}
            />
            <Button
              variant="contained"
              startIcon={<UploadIcon />}
              onClick={() => fileInputRef.current?.click()}
              disabled={loading}
            >
              {loading ? 'Parsing...' : 'Choose File'}
            </Button>
            {loading && <LinearProgress sx={{ mt: 2 }} />}
          </CardContent>
        </Card>
      )}

      {/* Step 1: Map Columns */}
      {step === 1 && preview && (
        <Box>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
                File: {file?.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {preview.total_rows} rows found. Map each CSV column to the corresponding field.
              </Typography>
            </CardContent>
          </Card>

          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
                Column Mapping
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 2 }}>
                {preview.headers.map((header, idx) => (
                  <FormControl key={idx} size="small" fullWidth>
                    <InputLabel>{header}</InputLabel>
                    <Select
                      value={columnMap[String(idx)] || ''}
                      onChange={(e) => {
                        setColumnMap((prev) => ({
                          ...prev,
                          [String(idx)]: e.target.value as string,
                        }));
                      }}
                      label={header}
                    >
                      {FIELD_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ))}
              </Box>
            </CardContent>
          </Card>

          {/* Preview Table */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
                Preview (first {preview.sample_rows.length} rows)
              </Typography>
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {preview.headers.map((h, i) => (
                        <TableCell key={i}>
                          <Box>
                            <Typography variant="caption" color="text.secondary">{h}</Typography>
                            {columnMap[String(i)] && (
                              <Chip label={columnMap[String(i)]} size="small" color="primary" sx={{ ml: 1, height: 18, fontSize: '0.65rem' }} />
                            )}
                          </Box>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {preview.sample_rows.map((row, ri) => (
                      <TableRow key={ri}>
                        {row.map((cell, ci) => (
                          <TableCell key={ci}>
                            <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>{cell}</Typography>
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </CardContent>
          </Card>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button onClick={handleReset}>Cancel</Button>
            <Button
              variant="contained"
              onClick={() => setStep(2)}
              disabled={!hasDate || !hasAmount}
            >
              Next: Configure Import
            </Button>
          </Box>
          {(!hasDate || !hasAmount) && (
            <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block', textAlign: 'right' }}>
              You must map "Date" and at least one amount column (Amount, or
              Debit and Credit)
            </Typography>
          )}
        </Box>
      )}

      {/* Step 2: Confirm */}
      {step === 2 && (
        <Card>
          <CardContent>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 3 }}>
              Import Settings
            </Typography>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 3 }}>
              <FormControl fullWidth>
                <InputLabel>Account *</InputLabel>
                <Select
                  value={accountId}
                  onChange={(e) => setAccountId(e.target.value)}
                  label="Account *"
                >
                  {accounts.map((a) => (
                    <MenuItem key={a.id} value={a.id}>
                      {a.name} ({a.account_type})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Date Format</InputLabel>
                <Select
                  value={dateFormat}
                  onChange={(e) => setDateFormat(e.target.value)}
                  label="Date Format"
                >
                  {DATE_FORMATS.map((f) => (
                    <MenuItem key={f.value} value={f.value}>
                      {f.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ mt: 3 }}>
              <TextField
                fullWidth
                label="Closing balance on the statement"
                placeholder="e.g. 4200.00"
                value={closingBalance}
                onChange={(e) => setClosingBalance(e.target.value)}
                helperText={
                  hasBalanceColumn
                    ? 'Optional. Leave blank to use the running balance column from the file.'
                    : 'Optional, but without it this account will only show the net ' +
                      'movement of these rows, not its real balance. Enter the figure ' +
                      'exactly as the statement prints it — for a credit card that is ' +
                      'the amount owed.'
                }
              />
            </Box>

            <Box sx={{ mt: 2 }}>
              <FormControlLabel
                control={<Checkbox checked={skipDupes} onChange={(e) => setSkipDupes(e.target.checked)} />}
                label="Skip duplicate transactions (same date + amount)"
              />
            </Box>

            <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="body2" fontWeight={600}>
                Summary
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {allRows.length} rows to import | {mappedFieldCount} columns mapped
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
              <Button onClick={() => setStep(1)}>Back</Button>
              <Button
                variant="contained"
                onClick={handleImport}
                disabled={loading || !accountId}
              >
                {loading ? <CircularProgress size={20} /> : `Import ${allRows.length} Transactions`}
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Step 3: Results */}
      {step === 3 && result && (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h5" fontWeight={700} color="success.main" sx={{ mb: 2 }}>
              Import Complete
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 4, mb: 3 }}>
              <Box>
                <Typography variant="h4" fontWeight={700} color="success.main">{result.imported}</Typography>
                <Typography variant="body2" color="text.secondary">Imported</Typography>
              </Box>
              <Box>
                <Typography variant="h4" fontWeight={700} color="warning.main">{result.skipped}</Typography>
                <Typography variant="body2" color="text.secondary">Skipped</Typography>
              </Box>
              {/* "Posted" is the number that actually moved a balance. It is
                  worth its own tile because it used to be zero for every
                  import ever run: the old importer wrote transactions and
                  never touched the ledger. */}
              {result.posted !== undefined && (
                <Box>
                  <Typography variant="h4" fontWeight={700}>{result.posted}</Typography>
                  <Typography variant="body2" color="text.secondary">Posted to ledger</Typography>
                </Box>
              )}
            </Box>
            {result.opening_posted && (
              <Alert severity="success" sx={{ mb: 2, textAlign: 'left' }}>
                This account now matches the closing balance you entered. Its
                balance and your net worth reflect the whole account, not just
                these rows.
              </Alert>
            )}
            {result.errors && result.errors.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2, textAlign: 'left' }}>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                  Issues ({result.errors.length})
                </Typography>
                <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                  {result.errors.map((e, i) => (
                    <Typography key={i} variant="body2">{e}</Typography>
                  ))}
                </Box>
              </Alert>
            )}
            <Button variant="contained" onClick={handleReset}>
              Import Another File
            </Button>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

// ---- Export Tab ----

const ExportTab: React.FC = () => {
  const [accountId, setAccountId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  React.useEffect(() => {
    const fetchAccounts = async () => {
      try {
        const svc = new AccountsService();
        const res = await svc.getAccounts({ per_page: 100 });
        setAccounts(res.data.data || []);
      } catch {
        // silent
      }
    };
    fetchAccounts();
  }, []);

  const handleExport = async () => {
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      await ImportExportService.exportCSV({
        account_id: accountId || undefined,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
      });
      setSuccess(true);
    } catch (err: any) {
      setError(err.message || 'Export failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" fontWeight={600} sx={{ mb: 3 }}>
          Export Transactions
        </Typography>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2, mb: 3 }}>
          <FormControl fullWidth>
            <InputLabel>Account (optional)</InputLabel>
            <Select
              value={accountId}
              onChange={(e) => setAccountId(e.target.value)}
              label="Account (optional)"
            >
              <MenuItem value="">All Accounts</MenuItem>
              {accounts.map((a) => (
                <MenuItem key={a.id} value={a.id}>
                  {a.name} ({a.account_type})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Start Date"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            label="End Date"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(false)}>
            CSV file downloaded successfully
          </Alert>
        )}

        <Button
          variant="contained"
          startIcon={loading ? <CircularProgress size={16} /> : <DownloadIcon />}
          onClick={handleExport}
          disabled={loading}
        >
          {loading ? 'Exporting...' : 'Download CSV'}
        </Button>
      </CardContent>
    </Card>
  );
};

// Simple CSV line parser handling quoted fields
function parseCSVLine(line: string): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"' && line[i + 1] === '"') {
        current += '"';
        i++;
      } else if (ch === '"') {
        inQuotes = false;
      } else {
        current += ch;
      }
    } else {
      if (ch === '"') {
        inQuotes = true;
      } else if (ch === ',') {
        result.push(current.trim());
        current = '';
      } else {
        current += ch;
      }
    }
  }
  result.push(current.trim());
  return result;
}
